import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useTranslation } from 'react-i18next';

const Navigation = ({ onTradeClick }) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open to prevent background scrolling
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { name: t('nav.home'), href: "/" },
    { name: t('nav.market'), href: "/#market" },
    { name: t('nav.calculator'), href: "/#calculator" },
    { name: t('nav.testimonials'), href: "/#testimonials" },
    { name: t('nav.contact'), href: "/#contact" },
    { name: t('nav.blog'), href: "/blog" },
    { name: t('nav.faq'), href: "/#faq" }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full ${isMenuOpen ? "z-[90]" : "z-50"} transition-all duration-500 ${isScrolled ? "py-4 bg-bg-deep/80 backdrop-blur-xl border-b border-glass-border shadow-lg" : "py-8 bg-transparent"}`}>
        <div className="container flex justify-end md:justify-between items-center">
          {/* Logo - Hidden on mobile, visible on desktop */}
          <Link to="/" className="hidden md:flex items-center group">
            <img
              src="/assets/logos/logo-navbar.png"
              alt="Zaptopay Official Logo"
              className="h-16 md:h-24 w-auto object-contain hover:scale-105 transition-transform duration-300 drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]"
             loading="lazy" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="text-[10px] font-black text-white/70 hover:text-primary transition-all tracking-[0.12em] uppercase"
              >
                {link.name}
              </Link>
            ))}

            <div className="h-6 w-px bg-glass-border mx-2"></div>

            <button
              onClick={onTradeClick}
              className="px-8 py-4 bg-[#00ff88] text-black text-xs font-black uppercase tracking-[0.12em] hover:bg-white transition-all shadow-[0_0_30px_rgba(0,255,136,0.6)] hover:shadow-[0_0_50px_rgba(0,255,136,0.8)] animate-pulse-whatsapp"
            >
              {t('nav.tradeNow')}
            </button>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-4 md:hidden relative z-[101]">
            <button
              className="w-10 h-10 glass border-glass-border flex flex-col items-center justify-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Backdrop */}
      <div 
        className={`md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity duration-500 ${isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu Drawer */}
      <div className={`md:hidden fixed top-0 right-0 h-full w-[85%] max-w-sm bg-[#0a0e27] border-l border-white/5 z-[101] flex flex-col px-6 pb-8 transition-transform duration-500 ease-in-out shadow-[-20px_0_50px_rgba(0,0,0,0.8)] overflow-y-auto ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
           style={{ paddingTop: 'max(2rem, env(safe-area-inset-top))' }}>
        
        <div className="flex justify-between items-center mb-10 mt-6">
            <img
              src="/assets/logos/logo-navbar.png"
              alt="Zaptopay Logo"
              className="h-10 w-auto object-contain drop-shadow-[0_0_10px_rgba(0,255,136,0.3)]"
             loading="lazy" />
            <button
              className="w-10 h-10 glass border-white/10 flex items-center justify-center rounded-full text-white hover:text-primary hover:bg-white/5 transition-all"
              onClick={() => setIsMenuOpen(false)}
            >
              <X size={18} />
            </button>
        </div>

        <div className="flex flex-col gap-6 flex-1 mt-4">
            {navLinks.map((link, i) => (
              <Link
                  key={link.name}
                  to={link.href}
                  className={`text-lg font-black tracking-widest uppercase text-white/90 hover:text-primary hover:translate-x-2 transition-all duration-500 delay-[${i * 50}ms] pb-4 border-b border-white/5 transform ${isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                  onClick={() => setIsMenuOpen(false)}
              >
                  {link.name}
              </Link>
            ))}
        </div>

        <div className="mt-8 pt-8 border-t border-white/10">
            <button
              onClick={() => { onTradeClick(); setIsMenuOpen(false); }}
              className={`w-full py-5 bg-[#00ff88] text-black text-xs font-black uppercase tracking-[0.12em] hover:bg-white transition-all duration-500 delay-300 shadow-[0_0_30px_rgba(0,255,136,0.2)] animate-pulse-whatsapp rounded-xl transform ${isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
            >
              {t('nav.tradeNow')}
            </button>
        </div>
      </div>
    </>
  );
};

export default Navigation;